import React from "react";
import './FormWithScanner.css';
import InputIdentification from "./InputIdentification";
import FormIdentification from "./FormIdentification";
import Scan from "../../hardware/Scan";
import {AElement} from "../../../models/elements/AElement";
import {EElementType} from "../../../models/utils/EElementType";
import {EnergyBox} from "../../../models/elements/EnergyBox";
import StickerService from "../../../services/StickerService";
import {WithTranslation, withTranslation} from "react-i18next";

/**
* Interface inheriting the translation module.
* We put all the props of the component there.
**/
interface Props extends WithTranslation {
    /**
     * Form title.
     */
    title?: string,
    /**
     * Current element to be identified.
     */
    initialElement: AElement,
    /**
     * Function called when the element is modified.
     * @param {AElement} element Updated element.
     */
    setElement: (element: AElement) => void,
}

/**
 * Component allowing to fill a form manually or with a label scanner.
 * @author Alizée Grand
 * @version 1.1
 * @creation 25/04/2022
 * @last_update 10/05/2022
 **/
class FormWithScanner extends React.Component<Props> {

    render() {

        /**
* Retrieves from the props the function to access the translation.
**/
const getTranslation = this.props.t;

        /**
         * Changes the serial number of the element to be identified.
         * @param {string} serialNumber New serial number.
         * @param {boolean} manually Indicates if the field was filled manually (true) or scanned (false).
         */
        const setSerialNumber = (serialNumber: string, manually: boolean) => {
            const updatedElement = this.props.initialElement;
            updatedElement.serialNumber = serialNumber;
            if(!manually) {
                updatedElement.isScanned = true;
            }
            this.props.setElement(updatedElement);
        }

        /**
         * Changes the LoRa ID of the energy box to be identified.
         * @param {string} loraId New LoRa ID.
         * @param {boolean} manually Indicates if the field was filled manually (true) or scanned (false).
         */
        const setLoraId = (loraId: string, manually: boolean) => {
            const updatedElement = this.props.initialElement as EnergyBox;
            updatedElement.eLoraId = loraId;
            if(!manually) {
                updatedElement.isScanned = true;
            }
            this.props.setElement(updatedElement);
        }

        /**
         * Generates the inputs to be filled in to identify the element.
         * @return {React.ReactNode[]} Input list.
         */
        const getInputs = (): React.ReactNode[] => {
            let array = [
                <InputIdentification
                    value={this.props.initialElement.serialNumber}
                    onNewValue={setSerialNumber}
                    label={getTranslation("steps.identification.form.label.serial")}
                />
            ];
            if(this.props.initialElement.type === EElementType.EnergyBox) {
                const energyBox = this.props.initialElement as EnergyBox;
                array.push(
                    <InputIdentification
                        value={energyBox.eLoraId}
                        onNewValue={setLoraId}
                        label={getTranslation("steps.identification.form.label.lora")}
                    />
                );
            }
            return array;
        }

        /**
         * Function called when the user scans a label. Checks the retrieved data.
         * @param {string} data Data recovered by the scanner.
         * @return {boolean} True if the data matches the requested field, false otherwise.
         */
        const scan = (data: string): boolean => {
            let scannedRight;
            if(this.props.initialElement.type === EElementType.EnergyBox) {
                scannedRight = scanEnergyBox(data);
            } else {
                scannedRight = scanElement(data);
            }
            if(scannedRight && this.props.initialElement.isCorrect()) {
                this.props.setElement(this.props.initialElement);
            }
            return scannedRight;
        }

        /**
         * Processes the data of a scanned element.
         * @param {string} data Scanned data.
         * @return {boolean} Checks if the scanned data matches the requested format for the element. Returns true if it does, false otherwise.
         */
        const scanElement = (data: string): boolean => {
            const updatedElement = this.props.initialElement;
            updatedElement.serialNumber = data;
            updatedElement.isScanned = true;
            return updatedElement.isSerialNumberRightFormat();
        }

        /**
         * Processes the data of a scanned energy box.
         * @param {string} data Scanned data.
         * @return {boolean} Checks if the scanned data matches the requested format for the energy box. Returns true if it does, false otherwise.
         */
        const scanEnergyBox = (data: string): boolean => {
            let scannedRight = false;
            let dataScanned = StickerService.decodeSticker(data);
            const element = this.props.initialElement as EnergyBox;
            // TODO: Rendre l'un ou l'autre facultatif
            if(dataScanned !== undefined) {
                element.isScanned = true;
                element.eLoraId = dataScanned.loraId !== undefined ? dataScanned.loraId : "";
                element.serialNumber = dataScanned.serialNumber !== undefined ? dataScanned.serialNumber : "";
                scannedRight = true;
            }
            return scannedRight;
        }

        return (

            <div>
                <p className="accent-word">{this.props.title}</p>
                <div id="form-with-scanner-container">
                    <div id="form-with-scanner-form">
                        <FormIdentification inputs={getInputs()}/>
                    </div>
                    <div id="form-with-scanner-button">
                        <Scan processScannedData={scan}/>
                    </div>
                </div>
            </div>

        );

    };

}

export default withTranslation()(FormWithScanner);
